

import { useId } from "react";
import { TableCellContentCTA } from "./components/TCCCTA";
import { DataType } from "@/types/enums/DataType";
import { Field } from "@/types/Field";
import { TableCellContentId } from "./components/TCCId";
import { TableCellContentBoolean } from "./components/TCCBoolean";
import { TableCellContentMultiSelect } from "./components/TCCMultiSelect";
import { TableCellContentSingleSelect } from "./components/TCCSingleSelect";
import { TableCellContentDateTime } from "./components/TCCDateTime";
import { TableCellContentDate } from "./components/TCCDate";
import { TableCellContentTime } from "./components/TCCTime";
import { TableCellContentUnknown } from "./components/TCCUnknown";
import { TableCellContentString } from "./components/TCCString";
import { TableCellContentLong } from "./components/TCCLong";
import { TableCellContentLocaleNumber } from "./components/TCCLocaleNumber";
import { TableCellContentEmail } from "./components/TCCEmail";
import { TableCellContentUrl } from "./components/TCCUrl";
import { TableCellContentObjectId } from "./components/TCCObjectId";
import { FormInput } from "../Inputs/FormInput";
import { TableCellContentGuestStatus } from "./components/TCCGuestStatus";
import { TableCellContentAction } from "./components/TCCAction";
import { TableCellContentCompanions } from "./components/TCCCompanions";
import { TCCCompanionsFilter } from "./components/TCCCompanionsFilter";
import { CompanionsTableCellContent } from "@/types/CompanionsTableCellContent";
import { AttachmentSummary } from "@/types/Attachment";
import { AppointmentSummary } from "@/types/AppointmentSummary";
import { Guest } from "@/types/Guest";
import { TCCAttachments } from "./components/TCCAttachments";
import { TCCSlotting } from "./components/TCCSlotting";
import { TCCSeating, type SeatingCellValue } from "./components/TCCSeating";
import { TableCellContentEmailStatus } from "./components/TCCEmailStatus";
import { Event } from "@/types/Event";
import { TableCellContentEventLimitMeta } from "./components/TCCEventLimitMeta";
import { TableCellContentCheckInOut } from "./components/TCCCheckInOut";
import { TCCCheckInStatus } from "./components/TCCCheckInStatus";
import {
  TCCCheckInLogAction,
  type CheckInActionValue,
} from "./components/TCCCheckInLogAction";
import { TCCCheckInLogCTA } from "./components/TCCCheckInLogCTA";
import { TableCellContentLimits } from "./components/TCCLimits";
import { TCCImage } from "./components/TCCImage";
import { AfImageValue } from "@/types/AfImage";
import { CustomLimits } from "@/types/CustomLimits";
import { EventLimitMetaTableCellContent } from "@/types/EventLimitMetaTableCellContent";
import { offersClearPick } from "@/lib/utils/guestClearFields";

interface TableCellContentProps {
  field: Field;
  isSelected: boolean;
  value: unknown;
  onChangeValueAction: (value: string | string[]) => void;
  viewOnly?: boolean;
  onFocusAction?: () => void;
  onBlurAction?: () => void; // Optional blur handler
  error?: string | null;
  isEditMode: boolean;
  border?: string;
  isFilter?: boolean;
  actionStatus?: string;
  height?: string;
  surface?: string;
  fontSize?: string;
  /**
   * Class for the READ-only rendering of a value (the non-editor branch).
   * `fontSize` reaches editors only, and the read branches disagree with each
   * other: TCCDateTime hardcodes `body-2-regular font-secondary` while
   * TCCSingleSelect and TCCLong carry no size or colour at all — uniform in a
   * table only because TableCell paints the <td>. A popup that renders read
   * values as pills needs ONE scale and ONE colour; the table keeps its
   * defaults by leaving this unset.
   */
  readTextClass?: string;
  padding?: string;
  rounded?: string;
  width?: string;
  iconSize?: number;
  archived?: boolean;
  copy?: boolean;
  blank?: boolean;
  /** EMAIL_STATUS-only: raw skip cause threaded to the status badge so a
   *  SKIPPED row can show why it was withheld. Undefined elsewhere. */
  skipReason?: string | null;
  /** EMAIL_STATUS-only: email went out without some/all attachments —
   *  renders the warning chip next to the status pill. */
  attachmentsDegraded?: boolean | null;
  /** EMAIL_STATUS-only: temporary refusals forwarded to the badge chip. */
  softBounces?: { at?: string; subType?: string }[] | null;
  /**
   * IANA timezone for DATE_TIME cells. Passthrough only — TCCDateTime falls
   * back to `useEventTimezone()` then the browser tz when omitted. NOTE: DATE
   * (date-only) cells IGNORE this — a pure calendar day is tz-invariant and is
   * always framed in UTC by TCCDate (see its DATE_FRAME_TZ).
   */
  timeZone?: string;
  /**
   * Optional test id forwarded to the underlying edit control (currently
   * SINGLE_SELECT / BOOLEAN → OptionInput). Enables stable e2e selectors like
   * `${testId}-option-${value}` on the value picker (e.g. bulk-edit value rows).
   */
  testId?: string;
  /** Raw SELECT option values that are full (per-option capacity reached) and
   *  should render greyed/non-pickable during value entry. Undefined = none.
   *  Only meaningful for additional SINGLE/MULTI_SELECT fields with capacity. */
  disabledOptions?: string[];
  /** IMAGE-only: guest whose field this cell edits — enables the upload flow
   *  (presign/confirm are guest-scoped). Threaded by the guest detail sheet;
   *  absent elsewhere (create dialog / table rows) → read-only / hint. */
  guestId?: string;
  /** IMAGE-only: true when hosted by the guest CREATE dialog — the guest-less
   *  edit hint then reads "upload after create" instead of the default
   *  "edit in guest details". */
  isCreateContext?: boolean;
  /** IMAGE-only: called INSTEAD of onChangeValueAction after a successful
   *  upload confirm (the value is persisted server-side at that point) —
   *  detail sheet syncs current + baseline, list hosts refresh the row. */
  onValuePersisted?: (value: AfImageValue) => void;
  onSeatPersisted?: () => void;
  /** IMAGE-only, list hosts: remove (X) clears immediately via a host-driven
   *  single-guest "" update instead of onChangeValueAction("") — the
   *  pending-save batch is never involved. */
  onImmediateRemove?: () => void | Promise<void>;
  /** EMAIL-only, edit mode: the box holds text that is not a storable address
   *  and the host's value therefore lags behind what is shown. See
   *  TableCellContentEmail.onInvalidChange. */
  onInvalidChange?: (invalid: boolean) => void;
}

export const TableCellContent: React.FC<TableCellContentProps> = ({
  field,
  isSelected,
  value,
  onChangeValueAction,
  onFocusAction,
  onBlurAction,
  actionStatus,
  error,
  isEditMode,
  border,
  isFilter,
  height,
  surface,
  fontSize,
  readTextClass,
  padding,
  rounded,
  archived,
  iconSize,
  disabledOptions,
  copy,
  blank,
  viewOnly,
  timeZone,
  skipReason,
  attachmentsDegraded,
  softBounces,
  testId,
  guestId,
  isCreateContext,
  onValuePersisted,
  onSeatPersisted,
  onImmediateRemove,
  onInvalidChange,
}) => {
  // Kanzlei K4-B — the filter box's id holds no part of the operand (it was
  // `edit-${value}`; FormInput writes it into `name` too, and Sentry copies
  // both into click breadcrumbs and INP span names).
  const filterInputId = `edit-${useId()}`;
  if (field?.dataType === DataType.UNKNOWN && isFilter)
    return (
      <FormInput
        id={filterInputId}
        border={border}
        rounded={rounded}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        margin=""
        height={height ?? "h-9"}
        fontSize={fontSize}
        surface={surface}
        disabled={viewOnly}
        paddingInput={padding}
        value={""}
        onChangeAction={() => {}}
      />
    );
  else if (field?.dataType === DataType.GUEST_STATUS)
    return (
      <TableCellContentGuestStatus
        options={field.options!}
        optionColors={field.optionColors}
        border={border}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        rounded={rounded}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        blank={blank}
      />
    );
  else if (field?.dataType === DataType.CTA)
    return (
      <TableCellContentCTA isEditing={isEditMode} event={value as Event} />
    );
  else if (field?.dataType === DataType.UNKNOWN)
    return <TableCellContentUnknown isEditing={isEditMode} value={value} />;
  else if (field?.dataType === DataType.STRING)
    return (
      <TableCellContentString
        border={border}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        rounded={rounded}
        editable={field.editable}
        isFilter={isFilter}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        copy={copy}
      />
    );
  else if (field?.dataType === DataType.LONG)
    return (
      <TableCellContentLong
        readTextClass={readTextClass}
        editable={field.editable}
        isFilter={isFilter}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        rounded={rounded}
        border={border}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
      />
    );
  else if (field?.dataType === DataType.DOUBLE)
    return (
      <TableCellContentLocaleNumber
        editable={field.editable}
        isFilter={isFilter}
        border={border}
        height={height}
        rounded={rounded}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
      />
    );
  else if (field?.dataType === DataType.CURRENCY)
    return (
      <TableCellContentLocaleNumber
        editable={field.editable}
        isFilter={isFilter}
        border={border}
        height={height}
        rounded={rounded}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        currencyCode={field.currencyCode}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
      />
    );
  else if (field?.dataType === DataType.EMAIL)
    return (
      <TableCellContentEmail
        editable={field.editable}
        isFilter={isFilter}
        border={border}
        value={value == null ? "" : String(value)}
        height={height}
        rounded={rounded}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        onInvalidChange={onInvalidChange}
      />
    );
  else if (field?.dataType === DataType.URL)
    return (
      <TableCellContentUrl
        editable={field.editable}
        isFilter={isFilter}
        border={border}
        value={value == null ? "" : String(value)}
        height={height}
        rounded={rounded}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        isSelected={isSelected}
        isEditing={isEditMode}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
      />
    );
  else if (field?.dataType === DataType.ID)
    return (
      <TableCellContentId
        isEditing={isEditMode}
        index={value == null ? "" : String(value)}
      />
    );
  else if (field?.dataType === DataType.DATE_TIME) {
    return (
      <TableCellContentDateTime
        readTextClass={readTextClass}
        fieldName={field.name}
        border={border}
        editable={field.editable}
        height={height}
        viewOnly={viewOnly}
        fontSize={fontSize}
        rounded={rounded}
        surface={surface}
        padding={padding}
        isFilter={isFilter}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        iconSize={iconSize}
        timeZone={timeZone}
        minDate={field.lowerBound as string | Date | undefined}
        maxDate={field.upperBound as string | Date | undefined}
      />
    );
  } else if (field?.dataType === DataType.TIME)
    return (
      <TableCellContentTime
        border={border}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        rounded={rounded}
        value={value == null ? "" : String(value)}
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        iconSize={iconSize}
      />
    );
  else if (field?.dataType === DataType.DATE)
    return (
      <TableCellContentDate
        border={border}
        editable={field.editable}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        isFilter={isFilter}
        value={value == null ? "" : String(value)}
        rounded={rounded}
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        iconSize={iconSize}
        timeZone={timeZone}
        minDate={field.lowerBound as string | Date | undefined}
        maxDate={field.upperBound as string | Date | undefined}
      />
    );
  else if (field?.dataType === DataType.BOOLEAN)
    return (
      <TableCellContentBoolean
        border={border}
        editable={field.editable}
        isFilter={isFilter}
        height={height}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        required={field.required ?? false}
        // SMS channel wave: no "—" for a built-in the backend never unsets
        // (smsOptIn — strict consent, see guestClearFields.ts).
        clearable={offersClearPick(field)}
                // PB-RP-1: `undefined` is preserved here, NOT coerced to "".
        //
        // Both TCC components below document the distinction they need —
        // "cleared" ("") shows the "—" sentinel selected, "untouched"
        // (undefined) shows nothing yet — and a blanket `value == null ? "" :
        // String(value)` sweep erased it. PBulkEdit seeds every row at
        // `undefined`, so an untouched row arrived pre-selected on "—";
        // `OptionInput.handleRecommendationClick` then swallowed the click as a
        // self-select (`if (value === inputValue) return;`), onChange never
        // fired, and Apply never armed. Clicking "—" on a field that was never
        // set was a DEAD CLICK — precisely what the sentinel was introduced to
        // prevent.
        //
        // Only these two branches carry that sentinel; the rest keep the
        // coercion, which is harmless for them.
        value={value == null ? undefined : String(value)}
        isSelected={isSelected}
        rounded={rounded}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        testId={testId}
      />
    );
  else if (field?.dataType === DataType.SINGLE_SELECT) {
    // Filter mode: render a multi-pick so a single-select field can be filtered
    // by several values at once ("language is German OR French"). The array
    // value is OR-expanded into one row per value at parse time (parser.ts).
    // Edit/display keep the single-value picker. fieldName drives the built-in
    // raw-value (timeZone) / always-set (guestType) handling inside TCCMultiSelect.
    if (isFilter)
      return (
        <TableCellContentMultiSelect
          border={border}
          editable={field.editable}
          viewOnly={viewOnly}
          isFilter={isFilter}
          height={height}
          rounded={rounded}
          fontSize={fontSize}
          surface={surface}
          padding={padding}
          additionalField={field.additionalField}
          options={field.options ?? []}
          optionColors={field.optionColors}
          dynamicLabels={field.optionLabels}
          optionNamespace={field.optionNamespace}
          optionsPending={field.optionsPending}
          // A filter pick is never "required": unchecking all must clear to the
          // "—" (is-empty) state, not snap back to the first option. (Relevant
          // for required built-ins like `state` now routed through multi-pick.)
          required={false}
          value={
            Array.isArray(value)
              ? (value as unknown[]).map(String)
              : value == null || value === ""
                ? []
                : [String(value)]
          }
          isSelected={isSelected}
          isEditing={isEditMode}
          onFocusAction={onFocusAction}
          onChangeValue={onChangeValueAction}
          error={error}
          fieldName={field.name}
        />
      );
    return (
      <TableCellContentSingleSelect
        readTextClass={readTextClass}
        border={border}
        editable={field.editable}
        isFilter={isFilter}
        viewOnly={viewOnly}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        additionalField={field.additionalField}
        required={field.required ?? false}
                // PB-RP-1: `undefined` is preserved here, NOT coerced to "".
        //
        // Both TCC components below document the distinction they need —
        // "cleared" ("") shows the "—" sentinel selected, "untouched"
        // (undefined) shows nothing yet — and a blanket `value == null ? "" :
        // String(value)` sweep erased it. PBulkEdit seeds every row at
        // `undefined`, so an untouched row arrived pre-selected on "—";
        // `OptionInput.handleRecommendationClick` then swallowed the click as a
        // self-select (`if (value === inputValue) return;`), onChange never
        // fired, and Apply never armed. Clicking "—" on a field that was never
        // set was a DEAD CLICK — precisely what the sentinel was introduced to
        // prevent.
        //
        // Only these two branches carry that sentinel; the rest keep the
        // coercion, which is harmless for them.
        value={value == null ? undefined : String(value)}
        rounded={rounded}
        options={field.options ?? []}
        optionColors={field.optionColors}
        dynamicLabels={field.optionLabels}
        optionNamespace={field.optionNamespace}
        optionsPending={field.optionsPending}
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        fieldName={field.name}
        testId={testId}
        disabledOptions={disabledOptions}
      />
    );
  }
  else if (field?.dataType === DataType.MULTI_SELECT)
    return (
      <TableCellContentMultiSelect
        border={border}
        editable={field.editable}
        viewOnly={viewOnly}
        isFilter={isFilter}
        height={height}
        rounded={rounded}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        additionalField={field.additionalField}
        options={field.options ?? []}
        optionColors={field.optionColors}
        dynamicLabels={field.optionLabels}
        optionNamespace={field.optionNamespace}
        optionsPending={field.optionsPending}
        required={field.required ?? false}
        // MULTI_SELECT upperBound is the max # of options a guest may pick.
        maxSelections={
          field.upperBound != null && Number.isFinite(Number(field.upperBound))
            ? Number(field.upperBound)
            : undefined
        }
        disabledOptions={disabledOptions}
        value={
          Array.isArray(value)
            ? (value as unknown[]).map(String)
            : value == null || value === ""
              ? []
              : [String(value)]
        }
        isSelected={isSelected}
        isEditing={isEditMode}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
      />
    );
  else if (field?.dataType === DataType.IMAGE)
    return (
      <TCCImage
        field={field}
        value={value}
        isEditing={isEditMode}
        isSelected={isSelected}
        isFilter={isFilter}
        viewOnly={viewOnly}
        onChangeValue={onChangeValueAction}
        onValuePersisted={onValuePersisted}
        onImmediateRemove={onImmediateRemove}
        guestId={guestId}
        isCreateContext={isCreateContext}
        height={height}
        border={border}
        rounded={rounded}
        surface={surface}
        fontSize={fontSize}
        padding={padding}
      />
    );
  else if (field?.dataType === DataType.CHECK_IN_STATUS) {
    if (isFilter) {
      const displayValue =
        value === true || value === "true"
          ? "CHECKED_IN"
          : value === false || value === "false"
            ? "CHECKED_OUT"
            : "";
      return (
        <TableCellContentSingleSelect
          border={border}
          editable={true}
          isFilter={isFilter}
          viewOnly={viewOnly}
          height={height}
          fontSize={fontSize}
          surface={surface}
          padding={padding}
          additionalField={false}
          required={false}
          value={displayValue}
          rounded={rounded}
          options={["CHECKED_IN", "CHECKED_OUT"]}
          isSelected={isSelected}
          isEditing={isEditMode}
          onFocusAction={onFocusAction}
          onChangeValue={(v) =>
            onChangeValueAction(
              v === "CHECKED_IN" ? "true" : v === "CHECKED_OUT" ? "false" : "",
            )
          }
          error={error}
        />
      );
    }
    return <TCCCheckInStatus value={value as boolean} />;
  } else if (field?.dataType === DataType.CHECK_IN_OUT) {
    if (isFilter) return null;
    return (
      <TableCellContentCheckInOut
        guestId={value ? String(value) : ""}
      />
    );
  } else if (field?.dataType === DataType.CHECK_IN_LOG_ACTION) {
    if (isFilter) {
      return (
        <TableCellContentSingleSelect
          border={border}
          editable={true}
          isFilter={isFilter}
          viewOnly={viewOnly}
          height={height}
          fontSize={fontSize}
          surface={surface}
          padding={padding}
          additionalField={false}
          required={false}
          value={value ? String(value) : ""}
          rounded={rounded}
          // The field's own option list (FIELDS_CHECK_IN_LOG in
          // default-returner) — this used to be a hardcoded pair, so every
          // action added to that list since (SESSION_CHECK_IN, and now
          // CHECK_IN_RESET) was defined but unofferable: the column showed
          // rows the filter could not ask for. Fallback keeps the old pair for
          // a field that carries no options at all.
          options={
            field.options && field.options.length > 0
              ? field.options
              : ["CHECK_IN", "CHECK_OUT"]
          }
          isSelected={isSelected}
          isEditing={isEditMode}
          onFocusAction={onFocusAction}
          onChangeValue={onChangeValueAction}
          error={error}
        />
      );
    }
    // The history row hands over `{action, undoOf?, undoneBy?}` (see
    // checkInActionValue in CheckInRows) so the chip can mark an undo row and
    // an undone one; any other producer still passes the bare action string.
    // Only this read-only branch takes the object — the filter branch above
    // keeps its string, a filter value is never an undo link.
    return (
      <TCCCheckInLogAction
        value={
          value && typeof value === "object"
            ? (value as CheckInActionValue)
            : value
              ? String(value)
              : ""
        }
      />
    );
  } else if (field?.dataType === DataType.CHECK_IN_LOG_CTA) {
    // `action` rides along so TCCCheckInLogCTA's "only CHECK_IN/CHECK_OUT"
    // guard has something to read — dropping it from this cast is what kept
    // that guard dead (see checkInCtaValue in CheckInRows).
    const ctaValue = value as {
      guestId: string;
      checkedIn: boolean | null;
      eventId: string;
      action?: string;
    };
    return <TCCCheckInLogCTA value={ctaValue} eventId={ctaValue?.eventId ?? ""} />;
  } else if (field?.dataType === DataType.ACTION) {
    return (
      <TableCellContentAction
        registrant={field.name === "actionRegistrant"}
        actionStatus={actionStatus ?? ""}
        archived={archived ?? false}
        id={value ? String(value) : (value as string)}
      />
    );
  } else if (field?.dataType === DataType.EVENT_LIMIT_META) {
    return (
      <TableCellContentEventLimitMeta
        value={value as EventLimitMetaTableCellContent}
      />
    );
  } else if (field?.dataType === DataType.LIMITS) {
    return <TableCellContentLimits value={value as CustomLimits} />;
  } else if (field?.dataType === DataType.COMPANIONS) {
    if (isFilter) {
      return (
        <TCCCompanionsFilter
          value={value ? String(value) : ""}
          onChangeValue={onChangeValueAction}
          onBlurAction={onBlurAction}
          border={border}
          height={height}
          fontSize={fontSize}
          surface={surface}
          padding={padding}
          rounded={rounded}
        />
      );
    }
    return (
      <TableCellContentCompanions
        isEditing={isEditMode}
        companions={value as CompanionsTableCellContent}
      />
    );
  } else if (field?.dataType === DataType.ATTACHMENTS) {
    return (
      <TCCAttachments
        value={(value as AttachmentSummary[]) || []}
        isEditing={isEditMode}
      />
    );
  } else if (field?.dataType === DataType.SLOTTING) {
    return (
      <TCCSlotting
        value={(value as AppointmentSummary[]) || []}
        isEditing={isEditMode}
      />
    );
  } else if (field?.dataType === DataType.SEATING) {
    return (
      <TCCSeating
        value={value as SeatingCellValue}
        isEditing={isEditMode}
        // The IMAGE channel, for the same reason IMAGE has it: a seat is not a
        // field of the guest but a row in a furniture document, written in its
        // own transaction with its own mail. It must never reach the pending-
        // edit batch — `parseGuest` has no case for it and would drop it in
        // silence — so the cell reports "persisted" instead of "changed".
        onValuePersisted={onSeatPersisted}
        isFilter={isFilter}
        viewOnly={viewOnly}
      />
    );
  } else if (field?.dataType === DataType.EMAIL_STATUS) {
    return (
      <TableCellContentEmailStatus
        options={field.options!}
        value={value == null ? "" : String(value)}
        isFilter={isFilter}
        border={border}
        height={height}
        viewOnly={viewOnly}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        rounded={rounded}
        onFocusAction={onFocusAction}
        onChangeValue={onChangeValueAction}
        error={error}
        skipReason={skipReason}
        attachmentsDegraded={attachmentsDegraded}
        softBounces={softBounces}
      />
    );
  } else if (field?.dataType === DataType.OBJECT_ID)
    return (
      <TableCellContentObjectId
        border={border}
        onChangeValue={onChangeValueAction}
        height={height}
        fontSize={fontSize}
        surface={surface}
        padding={padding}
        viewOnly={viewOnly}
        rounded={rounded}
        isEditing={isEditMode}
        value={value == null ? "" : String(value)}
        isFilter={isFilter}
        onBlurAction={onBlurAction}
        onFocusAction={onFocusAction}
      />
    );
};
